import { api } from './base';
import moment from 'moment';
import { Employee } from '@/api/employee.api';
import { SimpleDepartment, SiteRole } from '@/api/site_admin_role.api';

export interface SitePerm {
  id: number;
  status: number;
  name: string;
  title: string;
  description?: string;
  site_id: number;
  rw_sep: boolean;
  group_id?: number;

  expire_time?: string;
  role_checked?: boolean;
  role_writable?: boolean;
  employee_checked?: boolean;
  employee_writable?: boolean;
}

export interface PermGroup {
  id?: number;
  name: string;
  perms: Array<SitePerm>;
}

export interface PermMembers {
  departments: Array<SimpleDepartment>;
  employees: Array<Employee>;
  roles?: Array<SiteRole>;
  all_employees?: Array<Employee>;
}

export interface SitePerms {
  groups: Array<PermGroup>;
}

export const siteAdminPermApis = {
  getSitePerms(siteId: number): Promise<SitePerms> {
    return api.get(`/admin/sites/${siteId}/perms`);
  },
  savePermGroup(siteId: number, group: PermGroup): Promise<PermGroup> {
    if (group.id) {
      return api.put(`/admin/sites/${siteId}/perm_groups/${group.id}`, group).then(result => result.perm_group);
    } else {
      return api.post(`/admin/sites/${siteId}/perm_groups`, group).then(result => result.perm_group);
    }
  },
  deletePermGroup(siteId: number, groupId: number): Promise<void> {
    return api.delete<void>(`/admin/sites/${siteId}/perm_groups/${groupId}`);
  },
  savePerm(siteId: number, perm: SitePerm): Promise<SitePerm> {
    if (perm.id) {
      return api.put(`/admin/sites/${siteId}/perms/${perm.id}`, perm).then(result => result.perm);
    } else {
      return api.post(`/admin/sites/${siteId}/perms`, perm).then(result => result.perm);
    }
  },
  deletePerm(siteId: number, permId: number) {
    return api.delete<void>(`/admin/sites/${siteId}/perms/${permId}`);
  },
  getPermState(
    siteId: number,
    { employeeId, roleId }: { employeeId?: number; roleId?: number }
  ): Promise<Array<PermGroup>> {
    let url = `/admin/sites/${siteId}/perms`;
    if (employeeId) {
      url += `?employee_id=${employeeId}`;
    } else if (roleId) {
      url += `?role_id=${roleId}`;
    }
    return api.get(url).then(result => result.groups);
  },
  grant(siteId: number, groups: Array<PermGroup>, { employeeId, roleId }: { employeeId?: number; roleId?: number }) {
    const perms = groups
      .map(group =>
        group.perms.map(perm => ({
          id: perm.id,
          employee_checked: perm.employee_checked,
          role_checked: perm.role_checked,
          employee_writable: perm.employee_writable,
          role_writable: perm.role_writable,
          expire_time: perm.expire_time ? moment(new Date(perm.expire_time as string)).format('YYYY-MM-DD') : null
        }))
      )
      .flat();
    return api.post<void>(`/admin/sites/${siteId}/perms/grants`, { employee_id: employeeId, role_id: roleId, perms });
  },
  addGrant(
    siteId: number,
    permId: number,
    writable: boolean,
    add_grant: boolean,
    { employeeId, roleId }: { employeeId?: number; roleId?: number }
  ) {
    return api.put<void>(`/admin/sites/${siteId}/perms/grants`, {
      employee_id: employeeId,
      role_id: roleId,
      perm_id: permId,
      writable: writable,
      add_grant: add_grant
    });
  },
  getPermMembers(siteId: number, permName: string) {
    return api.get(`/admin/sites/${siteId}/perms/${permName}/employees`);
  }
};
